import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Gift, Trophy, Zap, Shield, Target, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import logoDark from "@/assets/logo-dark.png";

const Promo = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState({ users: 0, events: 0, wagered: 0 });

  // Store UTM parameters on mount
  useEffect(() => {
    const utmSource = searchParams.get("utm_source");
    const utmMedium = searchParams.get("utm_medium");
    const utmCampaign = searchParams.get("utm_campaign");
    const utmContent = searchParams.get("utm_content");
    const utmTerm = searchParams.get("utm_term");

    if (utmSource || utmMedium || utmCampaign) {
      sessionStorage.setItem("utm_params", JSON.stringify({
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
        utm_term: utmTerm,
        landing_time: new Date().toISOString()
      }));
    }
  }, [searchParams]);

  // Fetch real stats using public function (no auth required)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data, error } = await supabase.rpc("get_platform_stats");
        
        if (error) {
          console.error("Failed to fetch platform stats:", error);
          // Use fallback values on error
          setStats({ users: 0, events: 0, wagered: 0 });
          return;
        }

        if (data && data.length > 0) {
          setStats({
            users: Number(data[0].users_count) || 0,
            events: Number(data[0].open_events_count) || 0,
            wagered: Number(data[0].total_wagered) || 0
          });
        }
      } catch (err) {
        console.error("Error fetching stats:", err);
        setStats({ users: 0, events: 0, wagered: 0 });
      }
    };
    fetchStats();
  }, []);

  const handleCTA = () => navigate(user ? "/#events" : "/auth");

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Minimal Header */}
      <header className="fixed top-0 left-0 right-0 z-50 py-4 px-6 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto flex justify-center">
          <img src={logoDark} alt="Callyout" className="h-8" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 px-4">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-success/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Urgency Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-success/10 border border-success/30 text-success"
          >
            <Gift size={18} />
            <span className="text-sm font-medium">{t("promo.urgencyBadge")}</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
          >
            <span className="text-gradient">{t("promo.headline")}</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto"
          >
            {t("promo.subheadline")}
          </motion.p>

          {/* Primary CTA */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Button
              variant="hero"
              size="xl"
              onClick={handleCTA}
              className={user ? "py-5 px-10 text-lg btn-glow-success" : "py-5 px-10 text-lg btn-glow-success flex-col h-auto"}
            >
              <span className="flex items-center gap-2 font-semibold">
                {user ? t("promo.startPredicting") : t("promo.ctaPrimary")}
                <ArrowRight size={22} />
              </span>
              {!user && <span className="text-sm opacity-80 font-normal">{t("promo.signUpFirst")}</span>}
            </Button>
          </motion.div>

          {/* No credit card badge */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-6 text-sm text-muted-foreground"
          >
            {t("promo.noCreditCard")}
          </motion.p>
        </div>
      </section>


      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-center mb-12"
          >
            {t("promo.benefitsTitle")}
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-6">
            <BenefitCard
              icon={<Gift size={32} />}
              title={t("promo.benefit1Title")}
              description={t("promo.benefit1Desc")}
              delay={0}
            />
            <BenefitCard
              icon={<Trophy size={32} />}
              title={t("promo.benefit2Title")}
              description={t("promo.benefit2Desc")}
              delay={0.1}
            />
            <BenefitCard
              icon={<Zap size={32} />}
              title={t("promo.benefit3Title")}
              description={t("promo.benefit3Desc")}
              delay={0.2}
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-muted/20">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-center mb-12"
          >
            {t("promo.howItWorksTitle")}
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            <StepCard step={1} title={t("promo.step1Title")} description={t("promo.step1Desc")} delay={0} />
            <StepCard step={2} title={t("promo.step2Title")} description={t("promo.step2Desc")} delay={0.1} />
            <StepCard step={3} title={t("promo.step3Title")} description={t("promo.step3Desc")} delay={0.2} />
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            <TrustBadge icon={<Shield size={20} />} text={t("promo.trustSecure")} />
            <TrustBadge icon={<Zap size={20} />} text={t("promo.trustInstant")} />
            <TrustBadge icon={<Target size={20} />} text={t("promo.trustFair")} />
            <TrustBadge icon={<Clock size={20} />} text={t("promo.trust247")} />
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-t from-primary/10 to-transparent">
        <div className="max-w-2xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            {t("promo.finalCtaTitle")}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground mb-8"
          >
            {t("promo.finalCtaSubtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Button
              variant="hero"
              size="xl"
              onClick={handleCTA}
              className="py-5 px-12 text-lg btn-glow-success"
            >
              {user ? t("promo.startPredicting") : t("promo.finalCtaButton")}
              <ArrowRight size={22} />
            </Button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-6 text-sm text-muted-foreground"
          >
            {t("promo.noCreditCard")}
          </motion.p>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="py-8 px-4 border-t border-border/50">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src={logoDark} alt="Callyout" className="h-6 opacity-60" />
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <a href="/terms" className="hover:text-foreground transition-colors">{t("promo.terms")}</a>
            <span>|</span>
            <a href="/privacy" className="hover:text-foreground transition-colors">{t("promo.privacy")}</a>
          </div>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Callyout
          </p>
        </div>
      </footer>
    </div>
  );
};

// Sub-components
const StatCard = ({ icon, value, suffix = "", label, delay }: { icon: React.ReactNode; value: number; suffix?: string; label: string; delay: number }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      className="text-center"
    >
      <div className="flex justify-center mb-2">{icon}</div>
      <div className="text-2xl md:text-4xl font-bold">
        {displayValue.toLocaleString()}{suffix}
      </div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </motion.div>
  );
};

const BenefitCard = ({ icon, title, description, delay }: { icon: React.ReactNode; title: string; description: string; delay: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
    className="glass-card p-6 text-center hover:border-primary/50 transition-colors"
  >
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground">{description}</p>
  </motion.div>
);

const StepCard = ({ step, title, description, delay }: { step: number; title: string; description: string; delay: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
    className="text-center"
  >
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold text-lg mb-4">
      {step}
    </div>
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground text-sm">{description}</p>
  </motion.div>
);

const TrustBadge = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <div className="flex items-center gap-2 text-muted-foreground">
    <span className="text-primary">{icon}</span>
    <span className="text-sm font-medium">{text}</span>
  </div>
);

export default Promo;
